<template>
    <div class="bg-viva-money-yellow min-height-full">
        <slot></slot>
        <fade-transition mode="out-in" :duration="500" class="">
            <searching-spinner
                v-if="form.searching"
                :form="form"
                :forename="customer.forename"
            ></searching-spinner>
            <div v-else>
                <slot name="header" :form="form" :customer="customer"></slot>
                <ValidationObserver
                    ref="formobserver"
                    tag="div"
                    class="form-container min-h-screen bg-"
                    :class="{ 'no-bg': form.journey_id == 5 }"
                >
                    <slot
                        name="form"
                        :submit="handlePageSubmit"
                        :form="form"
                        :customer="customer"
                        :getValidationRules="getValidationRules"
                        :getValidationMessages="getValidationMessages"
                        :validation-rules="validationRules"
                        :validation-messages="validationMessages"
                    ></slot>
                </ValidationObserver>
            </div>
        </fade-transition>
        <slot
            name="faq"
            :form="form"
            :customer="customer"
            :unsubscribe="unsubscribe"
        ></slot>
        <slot
            name="footer"
            :form="form"
            :customer="customer"
            :unsubscribe="unsubscribe"
        ></slot>
    </div>
</template>

<script>
import { debounce } from "debounce";
import { isArray, pick, isString, delay, toArray } from "underscore";
import { scrollToElementIfRequired } from "../../assets/js/form";
import SearchingSpinner from "./searching-spinner";
import { get } from "lodash";
import { setCookie, removeCookie } from "tiny-cookie";
import { has } from "underscore";
import { isEqual } from "underscore";
import * as Sentry from "@sentry/browser";

const axios = require("axios").default;
const moment = require("moment");
var VueScrollTo = require("vue-scrollto");

const SUBMISSION_TIMEOUT_MS = 1000 * 60 * 5;
let APPLICATION_SUBMITTED_AT = null;
let lastSubmitErrors = null;

export default {
    provide() {
        return {
            $getValidationRules: this.getValidationRules,
            $getValidationMessages: this.getValidationMessages,
            $globals: this.globals,
            $customer: this.customerData,
        };
    },
    components: {
        "searching-spinner": SearchingSpinner,
        "fade-transition": () => import("./../../components/fade-transition"),
    },
    mounted() {
        this.$set(this.form, "searching", false);
        this.$set(this.form, "submitting", false);
        this.$set(this.form, "postCompleteAction", 'none');
        this.$set(this.form, "postCompleteLender", 'none');
        this.$set(this.form, "showDefaultSpinnerText", true);
        if (this.form.journey_id == 4) {
            this.$set(this.customer, "forceShowNextPayDate", true);
            this.$set(this.customer, "forceShowFollowingPayDate", true);
        }
        setTimeout(() => {
            document.body.classList.add("loaded");
        }, 100);
        setTimeout(() => {
            this.showBubbles = true;
        }, 1000);

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: "form",
            formType: this.form.journey_id,
            formPage: 1,
        });

        this.setupFormTiming();
    },
    data: function () {
        return {
            formStarted: new Date(),
            showBubbles: false,
            customer: this.customerData || {},
            form: this.formConfig || {},
            showNextPayDate: false,
            showFollowingPayDate: false,
            showHeader: false,
            defaultExit: "/?form-error",
        };
    },
    props: {
        firstPage: {
            default: "",
        },
        lastPage: {
            default: "",
        },
        customerData: {
            default: {},
        },
        formConfig: {
            default: {},
        },
        validationRules: {
            default: {},
        },
        validationMessages: {
            default: {},
        },
        globals: {
            default: {},
        },
    },
    watch: {
        'form.searching': function (newValue, oldValue) {
            if (newValue === true) {
                this.scrollToTop();
            }
        },
        "customer.dob": {
            immediate: true,
            deep: true,
            handler: function (newValue, oldValue) {
                let a = moment();
                let b = moment(newValue);
                let age = moment.duration(a.diff(b));
                let years = age.years();
                if (b.isValid()) {
                    this.$set(
                        this.customer,
                        "age",
                        "You are <strong>" + years + " years old</strong>"
                    );
                    this.$set(
                        this.customer,
                        "numeric_age",
                        years
                    );
                } else {
                    this.$set(this.customer, "age", null);
                }
            },
        },
        "customer.employment_status": {
            immediate: true,
            deep: true,
            handler: function (newValue, oldValue) {
                let map = {
                    emp_fulltime: "How often are you paid?",
                    emp_parttime: "How often are you paid?",
                    emp_self: "How often are you paid?",
                    student: "How often do you receive income?",
                    homemaker: "How often do you receive income?",
                    unemployed: "How often do you receive benefits?",
                    retired: "How often do you receive your pension?",
                    disability_benefits: "How often do you receive benefits?",
                    benefits: "How often do you receive benefits?",
                };
                this.$set(
                    this.customer,
                    "pay_frequency_label",
                    map[newValue || "student"] || map["student"]
                );

                // Set the industry
                if (
                    !this.customer.industry &&
                    newValue &&
                    ![
                        "emp_fulltime",
                        "emp_parttime",
                        "benefits_and_fulltime",
                        "benefits_and_parttime",
                        "armed_forces",
                        "emp_temporary",
                        "emp_self",
                    ].includes(newValue)
                ) {
                    this.customer.industry = "other_not_office_based";
                }

                // Employer
                if (
                    newValue &&
                    ![
                        "emp_fulltime",
                        "emp_parttime",
                        "benefits_and_fulltime",
                        "benefits_and_parttime",
                        "armed_forces",
                        "emp_temporary",
                    ].includes(newValue)
                ) {
                    let employerTitleMap = {
                        emp_self: "Self employed",
                        unemployed: "Unemployed",
                        benefits: "On Benefits",
                        student: "Student",
                        homemaker: "Homemaker",
                        retired: "Retired",
                    };
                    this.customer.employer =
                        employerTitleMap[newValue] || this.customer.employer;
                    this.customer.job_title =
                        employerTitleMap[newValue] || this.customer.job_title;
                } else {
                    let defaults = [
                        "Self employed",
                        "Unemployed",
                        "On Benefits",
                        "Student",
                        "Homemaker",
                        "Retired",
                    ];
                    if (defaults.includes(this.customer.employer)) {
                        this.customer.employer = null;
                    }
                    if (defaults.includes(this.customer.job_title)) {
                        this.customer.job_title = null;
                    }
                }
            },
        },
        "customer.residential_status": {
            immediate: true,
            deep: true,
            handler: function (newValue, oldValue) {
                let map = {
                    // Homeowner
                    homeowner:
                        "How much do you pay towards your mortgage each month?",
                    // Private Tenant
                    private_tenant: "How much do you pay towards the rent?",
                    // Council tenant
                    council_tenant: "How much do you pay towards the rent?",
                    // Living with parents
                    living_with_parents:
                        "How much do you pay towards the rent?",
                    // Other
                    other: "How much do you pay towards the rent?",
                };
                this.$set(
                    this.customer,
                    "housing_label",
                    map[newValue || "other"] || map["other"]
                );
            },
        },
    },
    methods: {
        scrollToTop() {
            window.scrollTo(0, 0);
        },
        sendAnalyticsEvent(category, action, label, value) {
            try {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                    event: "GAEvent",
                    eventCategory: category,
                    eventAction: action,
                    eventLabel: label,
                    eventValue: value,
                });
            } catch (e) {}
        },
        sendCommunicationPreferences() {
            if (isArray(this.customer.communication_preferences)) {
                try {
                    this.sendAnalyticsEvent(
                        "consent",
                        "email",
                        this.customer.communication_preferences.includes(
                            "email"
                        )
                            ? "accept"
                            : "reject",
                        0
                    );
                    this.sendAnalyticsEvent(
                        "consent",
                        "sms",
                        this.customer.communication_preferences.includes("sms")
                            ? "accept"
                            : "reject",
                        0
                    );
                    this.sendAnalyticsEvent(
                        "consent",
                        "whatsapp",
                        this.customer.communication_preferences.includes("whatsapp")
                            ? "accept"
                            : "reject",
                        0
                    );
                } catch (e) {}
            }
            if (has(this.customer, "marketing")) {
                this.sendAnalyticsEvent(
                    "consent",
                    "marketing",
                    this.customer.marketing ? "accept" : "reject",
                    0
                );
            }
        },
        sendFormTiming() {
            if (this.form.journey_id === 1) {
                this.sendAnalyticsEvent(
                    "app",
                    "secondstocomplete",
                    "secondstocomplete",
                    this.getSecondsInForm()
                );
            }
        },
        setupFormTiming() {
            $(document).one(
                "click keydown change",
                "input, select, .fancy-select",
                null,
                function () {
                    this.formStarted = new Date();
                }
            );
        },
        getSecondsInForm() {
            if (this.formStarted && this.formStarted.getTime) {
                return Math.round(
                    (new Date().getTime() - this.formStarted.getTime()) / 1000
                );
            } else {
                return 0;
            }
        },
        unsubscribe() {
            if (this.customer.unsubscribed) {
                return;
            }

            let promise = axios.post("/unsubscribe", {
                digid: this.customer.original_id,
            });

            promise.then((response) => {
                let unsubscribe = document.getElementById("unsubscribe");
                unsubscribe.textContent = response.data;
                this.customer.unsubscribed = true;
            });
        },
        getValidationRules(name) {
            if (isArray(name)) {
                return pick(this.validationRules, name);
            } else if (isString(name)) {
                return get(this.validationRules, name);
            }
            return {};
        },
        getValidationMessages(name) {
            if (isArray(name)) {
                return pick(this.validationMessages, name);
            } else if (isString(name)) {
                return get(this.validationMessages, name) || {};
            }
            return {};
        },
        logFrontendErrors(errors) {
            let data = this.customer;
            data.page = this.form.page;
            data.journey = this.form.journey;
            data.journey_type = this.form.journey_id;
            data.errors = errors;
            try {
                this.fireAmplitudeErrorEvents(errors);
            } catch (e) {}
            axios.post("/application-error", {
                errors: errors,
                input: data,
            }).catch(function (thrown) {
                // Do something with the error
            });
        },
        fireAmplitudeErrorEvents(errors) {
            Object.entries(errors).forEach(([key, value]) => {
                if (Array.isArray(value)) {
                    value.forEach((item) => {
                        window.amplitude.logFrontendErrors({
                            error: item,
                        });
                    });
                }
            });
        },
        setErrors(errors) {},
        handlePageSubmit() {
            this.$refs.formobserver.validate().then((result) => {
                if (result) {
                    try {
                        this.sendFormTiming();
                        this.sendCommunicationPreferences();
                    } catch (e) {}
                    this.amplitudeFormSubmission('applied');
                    this.submit();
                } else {
                    this.$nextTick(() => {
                        setTimeout(() => {
                            this.logFrontendErrors(
                                this.$refs.formobserver.errors
                            );
                        }, 250);
                        scrollToElementIfRequired(
                            document.querySelector(".invalid")
                        );
                    });
                    this.amplitudeFormSubmission('submit error');
                }
            });
        },
        amplitudeFormSubmission(status) {
            window.amplitude.formSubmissionEvent(status);
        },
        startCheckApplicationLoop() {
            var now = new Date().getTime();
            if (APPLICATION_SUBMITTED_AT == null) {
                APPLICATION_SUBMITTED_AT = new Date().getTime();
            }
            if (
                APPLICATION_SUBMITTED_AT != null &&
                now - APPLICATION_SUBMITTED_AT > SUBMISSION_TIMEOUT_MS
            ) {
                // Timeout reached, send to the
                // defaultRedirect(id);
                this.doDefaultRedirect();
            } else {
                setTimeout(() => {
                    this.checkApplication();
                }, 5000);
            }
        },
        checkApplication() {
            let digid = this.customer.digid;
            axios
                .post("/apply/check", {
                    id: digid,
                })
                .then((data) => {
                    data = data.data;
                    if (data.status === "processing") {
                        this.startCheckApplicationLoop();
                    }
                    if (data.status === "complete") {
                        // Get the url and redirect
                        if (data.url != null) {
                            this.fireConversions(
                                data.url,
                                digid,
                                data.commission
                            );
                            window.amplitude.formApplicationResponse({
                                decision: 'conversion',
                                commission: data.commission || 0,    
                        });
                            this.doCustomSignpostCheck(data.url);
                            this.doFinalRedirect(data.url, digid);
                        } else {
                            window.amplitude.formApplicationResponse({
                                decision: 'no conversion, default redirect',
                                commission: data.commission || 0,    
                        });
                            this.doDefaultRedirect();
                        }
                    }
                    if (
                        data.status === "error" &&
                        data.description === "no results"
                    ) {
                        window.amplitude.formApplicationResponse({
                            decision: 'error',
                            description: 'no results'    
                        });
                        // defaultRedirect(id);
                        this.doDefaultRedirect();
                    }
                    if (
                        data.status === "error" &&
                        data.description === "not in panel"
                    ) {
                        window.amplitude.formApplicationResponse({
                            decision: 'error',
                            description: 'not in panel'    
                        });
                        // defaultRedirect(id);
                        this.doDefaultRedirect();
                    }
                    // if (data.status === "error") {
                    //     this.doDefaultRedirect();
                    // }
                })
                .catch(() => {
                    this.startCheckApplicationLoop();
                });
        },
        doDefaultRedirect() {
            location.href = this.defaultExit;
        },
        fireConversions(url, id, commission) {
            commission = parseFloat(commission);
            if (isNaN(commission)) {
                commission = 0;
            }
            try {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                    event: "app",
                    appStatus: "accepted",
                    appCommission: commission,
                    appId: id,
                    email: String(this.customer.email || '').toLowerCase().trim(),
                    purpose: this.customer.purpose || "",
                    loanAmount: this.customer.loanamount || "",
                    loanTerm: this.customer.term || "",
                    employmentStatus: this.customer.employment_status || "",
                    formType: this.form.journey_id,
                });
                axios.get("/conv/" + id + "/" + commission);
            } catch (error) {
            }
        },
        doCustomSignpostCheck(url) {
            if(/moneywellness/.test(url)) {
                this.$set(this.form, "postCompleteAction", 'mwg-signpost');
                this.$set(this.form, "showDefaultSpinnerText", false);
            }
            if(/phonexa/.test(url)) {
                this.$set(this.form, "postCompleteAction", 'lender-signpost');
                this.$set(this.form, "postCompleteLender", 'Loans to Go');
                this.$set(this.form, "showDefaultSpinnerText", false);
            }
        },
        doFinalRedirect(url, id) {
            if ((this.form.journey_id || "1") != "5") {
                setCookie("vm2023_rc", id, { expires: "10m" });
                setCookie("vm_seen", "1", { expires: "2M" });
            } else {
                removeCookie("vm2023_rc");
            }
            setTimeout(() => {
                location.href = url;
            }, 3000);
        },
        submit() {
            if (this.form.submitting) {
                return;
            }
            this.form.submitting = true;

            let data = this.customer;
            data.page = this.form.page;
            data.journey = this.form.journey;
            data.journey_type = this.form.journey_id;
            axios
                .post("/apply", data, {
                    headers: {
                        "Content-Type": "application/json",
                    },
                })
                .then((response) => {
                    if (
                        response.status === 200 &&
                        response.data.status === "ok"
                    ) {
                        lastSubmitErrors = null;
                        this.form.submitting = false;
                        // Score the app
                        if (response.data.score) {
                            this.setEligibilityScore(response.data.score);
                        }
                        if (response.data.default) {
                            this.defaultExit = response.data.default;
                        }
                        // If we have digid the set it on the customer
                        if (response.data.digid) {
                            this.$set(
                                this.customer,
                                "digid",
                                response.data.digid
                            );
                        }

                        // Get this next page
                        let index = toArray(this.form.pages).indexOf(
                            this.form.page
                        );
                        if (toArray(this.form.pages)[index + 1]) {
                            // Change the page
                            this.$set(
                                this.form,
                                "page",
                                this.form.pages[index + 1]
                            );
                        } else {
                            this.$set(this.form, "searching", true);
                            if (
                                response.data.start_app_result &&
                                response.data.start_app_result.status == "ok"
                            ) {
                                this.startCheckApplicationLoop();
                            }
                        }
                    }
                })
                .catch((error) => {
                    this.form.submitting = false;
                    this.$nextTick(() => {
                        if (
                            error.response &&
                            error.response.status &&
                            error.response.data &&
                            error.response.data.errors
                        ) {
                            // Validation error
                            // Handle the validation errors
                            this.setValidationErrors(
                                error.response.data.errors
                            );
                        }
                    });
                })
                .then(() => {
                    this.form.submitting = false;
                    this.$nextTick(() => {
                        if (this.$refs.formobserver) {
                            this.$refs.formobserver.validate();
                        }
                    });
                });
        },
        setValidationErrors(errors) {
            delay(() => {
                if (isEqual(errors, lastSubmitErrors)) {
                    // We have had the same lot of errors twice, send an error to sentry
                    Sentry.captureException(new Error("Customer got he same validation error twice"), {
                        tags: {
                            section: "application-form-submit",
                        },
                        extra: {
                            errors: errors,
                            input: this.customer
                        }
                    });
                }
                lastSubmitErrors = errors;
                scrollToElementIfRequired(document.querySelector(".invalid"));
            }, 50);
        },
        setEligibilityScore(score, delay = 750) {
            setTimeout(() => {
                this.$set(this.customer, "eligibilityScore", score);
            }, delay);
        },
    },
};
</script>

<style lang="scss">
.top-bubble {
    opacity: 0;
    width: 338px;
    height: 338px;
    top: 0;
    right: 0;
    transform: translate(500px, -500px);
    border-radius: 50%;
    animation-name: top-bubble-grow;
    animation-duration: 5s;
    animation-fill-mode: forwards;
    animation-timing-function: cubic-bezier(0.175, 0.885, 0.32, 1.275);
    animation-delay: 2s;
    animation-iteration-count: 1;
    animation-direction: normal;
}

.right-bubble {
    opacity: 0;
    width: 305px;
    height: 305px;
    top: 33vh;
    right: -360px;
    transform: translate(305px, -50px);
    border-radius: 50%;
    animation-name: right-bubble-grow;
    animation-duration: 4s;
    animation-fill-mode: forwards;
    animation-timing-function: cubic-bezier(0.175, 0.885, 0.32, 1.275);
    animation-delay: 1.5s;
    animation-iteration-count: 1;
    animation-direction: normal;
}

.left-bubble {
    opacity: 0;
    width: 400px;
    height: 400px;
    top: 65vh;
    left: -360px;
    transform: translate(-400px, 400px);
    border-radius: 50%;
    animation-name: left-bubble-grow;
    animation-duration: 8s;
    animation-fill-mode: forwards;
    animation-timing-function: cubic-bezier(0.175, 0.885, 0.32, 1.275);
    animation-delay: 0.5s;
    animation-iteration-count: 1;
    animation-direction: normal;
}

@keyframes top-bubble-grow {
    0% {
        opacity: 0;
        transform: translate(500px, -500px);
    }
    25% {
        opacity: 1;
    }
    100% {
        opacity: 1;
        transform: translate(200px, -310px);
    }
}

@keyframes right-bubble-grow {
    0% {
        opacity: 0;
        transform: translate(305px, -50px);
    }
    25% {
        opacity: 1;
    }
    100% {
        opacity: 1;
        transform: translate(0px, 0px);
    }
}

@keyframes left-bubble-grow {
    0% {
        opacity: 0;
        transform: translate(-400px, 400px);
    }
    25% {
        opacity: 1;
    }
    100% {
        opacity: 1;
        transform: translate(-60px, 0px);
    }
}

// Out states, either initial
.app-form-page-change-enter {
    // position: absolute;
    opacity: 0;
    transform: translate(100%, 0);
    transform: translate3d(100%, 0, 0.0001%);
}
.app-form-page-change-leave-to {
    // position: absolute;
    opacity: 0;
    transform: translate(-100%, 0);
    transform: translate3d(-100%, 0, 0.0001%);
}

// Not sure what these states are for
.app-form-page-change-enter-to {
    transition: all 0.25s ease-out;
}
.app-form-page-change-leave {
    box-sizing: border-box;
}

// Classes while the transition is happening
.app-form-page-change-enter-active {
}
.app-form-page-change-leave-active {
    transition: all 0.25s ease-out;
}

.form-container {
    @apply bg-viva-money-yellow;

    &.no-bg {
        background-image: none;
    }
}
</style>
